import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api') + '/admin';

const moneyApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// Auth token injection
moneyApi.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Token refresh + 401 handling (mirrors reportsApi.ts pattern)
moneyApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken },
          );
          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return moneyApi(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

// ── Helper ────────────────────────────────────────────────────────────────────

/** Format integer cents as a dollar string, e.g. 1099 → "$10.99", -1234 → "-$12.34" */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return sign + '$' + (Math.abs(cents) / 100).toFixed(2);
}

/** Convert a dollar string/number from an input to integer cents */
export function dollarsToCents(dollars: string | number): number {
  return Math.round(parseFloat(String(dollars)) * 100);
}

// ── Expense categories (mirrors backend ExpenseService.EXPENSE_CATEGORIES) ───────

export const EXPENSE_CATEGORIES = [
  'parts', 'supplies', 'fuel', 'tools', 'subscriptions', 'other',
] as const;

// ── Types (match exact field names from PnlService + TechRateService + ExpenseService) ──

export type PnlRange = '7d' | '30d' | '90d' | '365d';

export interface CashSnapshot {
  shopId: number;
  estimateValueCents: number;
  completedWorkValueCents: number;
  accountsReceivableCents: number;
  collectedCents: number;
  estimateDays: number;
}

export interface PerJobProfitResult {
  workOrderId: number;
  shopId: number;
  /** Pre-tax revenue (total_amount − tax_amount). Sales tax is excluded from profit. */
  revenueCents: number;
  /** Sales tax collected (pass-through, shown for transparency, not profit). */
  taxCollectedCents: number;
  partCostCents: number;
  laborCostCents: number;
  processingFeeCents: number;
  jobExpenseCents: number;
  profitCents: number;
  marginFraction: number | null;
  hasMissingCost: boolean;
  breakdown: {
    laborHours: number | null;
    techRateCents: number | null;
    partCount: number;
    partsWithMissingCost: number;
    paymentCount: number;
  };
}

export interface DailyPnlRow {
  date: string;
  incomeCents: number;
  taxCollectedCents: number;
  cogsCents: number;
  expenseCents: number;
  netCents: number;
}

export interface PnlStatement {
  shopId: number;
  range: PnlRange;
  from: string;
  to: string;
  incomeCents: number;
  taxCollectedCents: number;
  cogsCents: number;
  expenseCents: number;
  netCents: number;
  daily: DailyPnlRow[];
}

export type ExpenseCategory = typeof EXPENSE_CATEGORIES[number];

export interface Expense {
  id: string;
  shopId: number;
  category: ExpenseCategory;
  amountCents: number;
  description: string | null;
  workOrderId: number | null;
  expenseDate: string;
  receiptAttachmentId: number | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface TechRate {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  costPerHourCents: number | null;
}

// ── Money read API ──────────────────────────────────────────────────────────────

export const getCashSnapshot = async (days = 5): Promise<CashSnapshot> => {
  const response = await moneyApi.get<CashSnapshot>('/money/snapshot', {
    params: { days },
  });
  return response.data;
};

export const getPerJobProfit = async (woId: number): Promise<PerJobProfitResult> => {
  const response = await moneyApi.get<PerJobProfitResult>(`/money/jobs/${woId}/profit`);
  return response.data;
};

export const getDailyPnl = async (range: PnlRange): Promise<DailyPnlRow[]> => {
  const response = await moneyApi.get<DailyPnlRow[]>('/money/daily', {
    params: { range },
  });
  return response.data;
};

export const getPnlStatement = async (range: PnlRange): Promise<PnlStatement> => {
  const response = await moneyApi.get<PnlStatement>('/money/pnl', {
    params: { range },
  });
  return response.data;
};

// ── Tech Rates ────────────────────────────────────────────────────────────────

export const getTechRates = async (): Promise<TechRate[]> => {
  const response = await moneyApi.get<TechRate[]>('/tech-rates');
  return response.data;
};

export const setTechRate = async (
  techUserId: number,
  costPerHourCents: number | null,
): Promise<TechRate> => {
  const response = await moneyApi.put<TechRate>('/tech-rates', {
    techUserId,
    costPerHourCents,
  });
  return response.data;
};

// ── Expenses ──────────────────────────────────────────────────────────────────

export const getExpenses = async (params?: {
  from?: string;
  to?: string;
  workOrderId?: number;
}): Promise<Expense[]> => {
  const response = await moneyApi.get<Expense[]>('/expenses', { params });
  return response.data;
};

export const createExpense = async (body: {
  category: ExpenseCategory;
  amountCents: number;
  description?: string | null;
  workOrderId?: number | null;
  expenseDate: string;
  receiptAttachmentId?: number | null;
}): Promise<Expense> => {
  const response = await moneyApi.post<Expense>('/expenses', body);
  return response.data;
};

export const updateExpense = async (
  expenseId: string,
  body: Partial<{
    category: ExpenseCategory;
    amountCents: number;
    description: string | null;
    workOrderId: number | null;
    expenseDate: string;
  }>,
): Promise<Expense> => {
  const response = await moneyApi.patch<Expense>(`/expenses/${expenseId}`, body);
  return response.data;
};

export const deleteExpense = async (expenseId: string): Promise<void> => {
  await moneyApi.delete(`/expenses/${expenseId}`);
};
